'use client';
import React, { useState, useMemo, useCallback } from 'react';
import Link from 'next/link';
import {
  ArrowDownTrayIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  FunnelIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  CameraIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  PencilSquareIcon,
  TrashIcon,
  ArrowPathIcon,
  DocumentTextIcon,
  ArrowLeftIcon,
} from '@heroicons/react/24/outline';
import {
  INITIAL_DOTS, DotPerson, CameraStatus, DotColor,
  getDotColor, formatRelativeTime, formatTimestamp,
  getCameraLabel, getColorLabel,
} from '@/data/dotsData';
import StatusBadge from '@/components/ui/StatusBadge';
import ReportCharts from './ReportCharts';

type SortField = 'dotId' | 'name' | 'cameras' | 'lastUpdate' | 'status';
type SortDir = 'asc' | 'desc';

export default function CameraReportScreen() {
  const [dots, setDots] = useState<DotPerson[]>(INITIAL_DOTS);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterColor, setFilterColor] = useState<'all' | DotColor>('all');
  const [filterCamera, setFilterCamera] = useState<'all' | CameraStatus>('all');
  const [sortField, setSortField] = useState<SortField>('dotId');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editCamera, setEditCamera] = useState<CameraStatus>('none');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingDot, setDeletingDot] = useState<DotPerson | null>(null);
  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  const showToast = useCallback((msg: string, type: 'success' | 'error' = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  }, []);

  // KPI counts
  const kpis = useMemo(() => {
    const blue   = dots.filter(d => getDotColor(d.lastUpdate) === 'blue').length;
    const yellow = dots.filter(d => getDotColor(d.lastUpdate) === 'yellow').length;
    const red    = dots.filter(d => getDotColor(d.lastUpdate) === 'red').length;
    const camA   = dots.filter(d => d.cameras === 'A' || d.cameras === 'both').length;
    const camB   = dots.filter(d => d.cameras === 'B' || d.cameras === 'both').length;
    const both   = dots.filter(d => d.cameras === 'both').length;
    const none   = dots.filter(d => d.cameras === 'none').length;
    return { blue, yellow, red, camA, camB, both, none, total: dots.length };
  }, [dots]);

  // Filtered + sorted
  const processed = useMemo(() => {
    let result = dots.filter(d => {
      const color = getDotColor(d.lastUpdate);
      if (filterColor !== 'all' && color !== filterColor) return false;
      if (filterCamera !== 'all' && d.cameras !== filterCamera) return false;
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        if (!d.name.toLowerCase().includes(q) && !d.dotId.includes(q)) return false;
      }
      return true;
    });

    result = [...result].sort((a, b) => {
      let cmp = 0;
      if (sortField === 'dotId') cmp = a.dotId.localeCompare(b.dotId);
      else if (sortField === 'name') cmp = a.name.localeCompare(b.name);
      else if (sortField === 'cameras') cmp = a.cameras.localeCompare(b.cameras);
      else if (sortField === 'lastUpdate') cmp = new Date(a.lastUpdate).getTime() - new Date(b.lastUpdate).getTime();
      else if (sortField === 'status') {
        const order: Record<DotColor, number> = { red: 0, yellow: 1, blue: 2 };
        cmp = order[getDotColor(a.lastUpdate)] - order[getDotColor(b.lastUpdate)];
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });

    return result;
  }, [dots, filterColor, filterCamera, searchQuery, sortField, sortDir]);

  const totalPages = Math.ceil(processed.length / perPage);
  const paginated = processed.slice((page - 1) * perPage, page * perPage);

  const handleSort = useCallback((field: SortField) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
    setPage(1);
  }, [sortField]);

  const toggleSelect = useCallback((id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    if (selectedIds.size === paginated.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(paginated.map(d => d.id)));
    }
  }, [selectedIds, paginated]);

  const handleBulkDelete = useCallback(() => {
    setDots(prev => prev.filter(d => !selectedIds.has(d.id)));
    showToast(`${selectedIds.size} punto(s) eliminado(s)`, 'error');
    setSelectedIds(new Set());
  }, [selectedIds, showToast]);

  const handleInlineEdit = useCallback((dot: DotPerson) => {
    setEditingId(dot.id);
    setEditCamera(dot.cameras);
  }, []);

  const handleInlineSave = useCallback((dot: DotPerson) => {
    setDots(prev => prev.map(d =>
      d.id === dot.id ? { ...d, cameras: editCamera, lastUpdate: new Date().toISOString() } : d
    ));
    setEditingId(null);
    showToast(`#${dot.dotId} actualizado`);
  }, [editCamera, showToast]);

  const handleDeleteConfirm = useCallback(() => {
    if (!deletingDot) return;
    setDots(prev => prev.filter(d => d.id !== deletingDot.id));
    setShowDeleteConfirm(false);
    showToast(`Punto #${deletingDot.dotId} eliminado`, 'error');
    setDeletingDot(null);
  }, [deletingDot, showToast]);

  const handleRefresh = useCallback((dot: DotPerson) => {
    setDots(prev => prev.map(d =>
      d.id === dot.id ? { ...d, lastUpdate: new Date().toISOString() } : d
    ));
    showToast(`#${dot.dotId} actualizado a ahora`);
  }, [showToast]);

  // CSV Export — backend integration point
  const handleExport = useCallback(() => {
    const rows = processed.map(d => {
      const color = getDotColor(d.lastUpdate);
      return [
        d.dotId, d.name,
        d.cameras === 'A' || d.cameras === 'both' ? 'Sí' : 'No',
        d.cameras === 'B' || d.cameras === 'both' ? 'Sí' : 'No',
        getColorLabel(color), formatTimestamp(d.lastUpdate),
        d.lat.toFixed(5), d.lng.toFixed(5),
      ].join(',');
    });
    const header = 'ID,Nombre,Cámara A,Cámara B,Estado,Última actualización,Latitud,Longitud';
    const csv = [header, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `pueblamapcamp-reporte-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    showToast(`Exportando ${processed.length} registros…`);
    // TODO: backend — POST /api/reports/export with filters payload
  }, [processed, showToast]);

  const activeFilters = (filterColor !== 'all' ? 1 : 0) + (filterCamera !== 'all' ? 1 : 0);

  return (
    <div className="h-full flex flex-col overflow-hidden bg-background">
      {/* Page header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-border shrink-0">
        <div className="flex items-center gap-3">
          <Link
            href="/"
            className="flex items-center justify-center w-8 h-8 rounded-lg border border-border text-muted-foreground hover:text-foreground hover:bg-muted transition-all duration-150"
            title="Volver al mapa"
          >
            <ArrowLeftIcon className="w-4 h-4" />
          </Link>
          <div>
            <h1 className="text-lg font-semibold text-foreground flex items-center gap-2">
              <DocumentTextIcon className="w-4.5 h-4.5 text-primary w-[18px] h-[18px]" />
              Reporte de Asignaciones de Cámara
            </h1>
            <p className="text-xs text-muted-foreground mt-0.5">
              {dots.length} personas registradas · Última actualización: {formatTimestamp(new Date().toISOString())}
            </p>
          </div>
        </div>
        <button
          onClick={handleExport}
          className="flex items-center gap-2 bg-primary text-primary-foreground rounded-lg px-4 py-2 text-sm font-medium hover:bg-primary/90 transition-all active:scale-95 shadow-lg"
        >
          <ArrowDownTrayIcon className="w-3.5 h-3.5" />
          Exportar CSV
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-6 py-4 flex flex-col gap-4">
        {/* KPI cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3">
          <KpiCard label="Total" value={kpis.total} color="neutral" />
          <KpiCard label="Activos" value={kpis.blue} color="blue" />
          <KpiCard label="Desconectados" value={kpis.yellow} color="yellow" />
          <KpiCard label="Fuera de línea" value={kpis.red} color="red" />
          <KpiCard label="Con Cámara A" value={kpis.camA} color="neutral" />
          <KpiCard label="Con Cámara B" value={kpis.camB} color="neutral" />
          <KpiCard label="Sin cámara" value={kpis.none} color="neutral" />
        </div>

        {/* Charts row */}
        <ReportCharts dots={dots} />

        {/* Table controls */}
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-2 bg-card border border-border rounded-lg px-3 py-2 flex-1 min-w-[200px] max-w-xs">
            <MagnifyingGlassIcon className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
            <input
              type="text"
              placeholder="Buscar por nombre o ID…"
              value={searchQuery}
              onChange={e => { setSearchQuery(e.target.value); setPage(1); }}
              className="bg-transparent text-sm text-foreground placeholder:text-muted-foreground outline-none flex-1"
            />
            {searchQuery && (
              <button onClick={() => { setSearchQuery(''); setPage(1); }} className="text-muted-foreground hover:text-foreground">
                <XMarkIcon className="w-3 h-3" />
              </button>
            )}
          </div>

          {/* Filter dropdown */}
          <div className="relative">
            <button
              onClick={() => setShowFilterPanel(p => !p)}
              className={`flex items-center gap-2 border rounded-lg px-3 py-2 text-sm font-medium transition-all ${
                activeFilters > 0 ? 'border-primary text-primary bg-primary/5' : 'border-border text-muted-foreground hover:text-foreground bg-card'
              }`}
            >
              <FunnelIcon className="w-3.5 h-3.5" />
              Filtrar
              {activeFilters > 0 && (
                <span className="bg-primary text-primary-foreground rounded-full w-4 h-4 flex items-center justify-center text-[10px] font-bold">
                  {activeFilters}
                </span>
              )}
            </button>
            {showFilterPanel && (
              <div className="absolute top-full left-0 mt-1 bg-card border border-border rounded-lg shadow-xl p-3 w-52 z-30">
                <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-2">Estado</p>
                <div className="flex flex-col gap-1 mb-3">
                  {(['all', 'blue', 'yellow', 'red'] as const).map(c => (
                    <button
                      key={`rfc-${c}`}
                      onClick={() => { setFilterColor(c); setPage(1); }}
                      className={`flex items-center gap-2 px-2 py-1.5 rounded-md text-sm transition-all ${
                        filterColor === c ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                      }`}
                    >
                      {c !== 'all' && <span className={`w-2 h-2 rounded-full ${c === 'blue' ? 'bg-blue-500' : c === 'yellow' ? 'bg-yellow-500' : 'bg-red-500'}`} />}
                      {c === 'all' ? 'Todos' : getColorLabel(c)}
                    </button>
                  ))}
                </div>
                <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-2">Cámara</p>
                <div className="flex flex-col gap-1">
                  {(['all', 'none', 'A', 'B', 'both'] as const).map(c => (
                    <button
                      key={`rfca-${c}`}
                      onClick={() => { setFilterCamera(c); setPage(1); }}
                      className={`flex items-center gap-2 px-2 py-1.5 rounded-md text-sm transition-all ${
                        filterCamera === c ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                      }`}
                    >
                      {c === 'all' ? 'Todas' : getCameraLabel(c)}
                    </button>
                  ))}
                </div>
                {activeFilters > 0 && (
                  <button
                    onClick={() => { setFilterColor('all'); setFilterCamera('all'); setPage(1); }}
                    className="mt-2 w-full text-xs text-muted-foreground hover:text-foreground border border-border rounded-md py-1.5"
                  >
                    Limpiar filtros
                  </button>
                )}
              </div>
            )}
          </div>

          <div className="ml-auto flex items-center gap-2 text-xs text-muted-foreground">
            <span>{processed.length} resultado(s)</span>
          </div>
        </div>

        {/* Bulk action bar */}
        {selectedIds.size > 0 && (
          <div className="flex items-center gap-3 bg-primary/10 border border-primary/20 rounded-lg px-4 py-2.5">
            <span className="text-sm font-medium text-primary">{selectedIds.size} seleccionado(s)</span>
            <div className="ml-auto flex items-center gap-2">
              <button
                onClick={handleBulkDelete}
                className="flex items-center gap-1.5 bg-red-500 text-white rounded-md px-3 py-1.5 text-xs font-medium hover:opacity-90 transition-all active:scale-95"
              >
                <TrashIcon className="w-3 h-3" />
                Eliminar seleccionados
              </button>
              <button
                onClick={() => setSelectedIds(new Set())}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                <XMarkIcon className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        )}

        {/* Table */}
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/30">
                  <th className="w-10 px-3 py-3">
                    <input
                      type="checkbox"
                      checked={selectedIds.size === paginated.length && paginated.length > 0}
                      onChange={toggleSelectAll}
                      className="w-3.5 h-3.5 accent-primary cursor-pointer"
                    />
                  </th>
                  <ThCell label="ID" field="dotId" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                  <ThCell label="Nombre" field="name" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                  <ThCell label="Cámara A" field="cameras" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                  <th className="px-3 py-3 text-left text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Cámara B</th>
                  <ThCell label="Estado" field="status" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                  <ThCell label="Última actualización" field="lastUpdate" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                  <th className="px-3 py-3 text-left text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {paginated.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-6 py-16 text-center">
                      <div className="flex flex-col items-center gap-3">
                        <MagnifyingGlassIcon className="w-8 h-8 text-muted-foreground/30" />
                        <p className="text-sm font-medium text-muted-foreground">No se encontraron puntos</p>
                        <p className="text-xs text-muted-foreground/60">Ajusta los filtros o el término de búsqueda</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  paginated.map((dot, i) => {
                    const color = getDotColor(dot.lastUpdate);
                    const hasA = dot.cameras === 'A' || dot.cameras === 'both';
                    const hasB = dot.cameras === 'B' || dot.cameras === 'both';
                    const isEditing = editingId === dot.id;
                    return (
                      <tr
                        key={dot.id}
                        className={`border-b border-border/50 transition-colors duration-150 hover:bg-muted/30 ${
                          selectedIds.has(dot.id) ? 'bg-primary/5' : i % 2 === 0 ? '' : 'bg-muted/10'
                        }`}
                      >
                        <td className="px-3 py-3">
                          <input
                            type="checkbox"
                            checked={selectedIds.has(dot.id)}
                            onChange={() => toggleSelect(dot.id)}
                            className="w-3.5 h-3.5 accent-primary cursor-pointer"
                          />
                        </td>
                        <td className="px-3 py-3">
                          <span className="font-mono font-semibold text-foreground text-xs bg-muted px-2 py-0.5 rounded">
                            #{dot.dotId}
                          </span>
                        </td>
                        <td className="px-3 py-3">
                          <span className="text-foreground font-medium">{dot.name}</span>
                        </td>
                        <td className="px-3 py-3">
                          {isEditing ? (
                            <select
                              value={editCamera}
                              onChange={e => setEditCamera(e.target.value as CameraStatus)}
                              className="bg-input border border-border rounded px-2 py-1 text-xs text-foreground outline-none focus:ring-1 focus:ring-ring"
                            >
                              <option value="none">Sin cámara</option>
                              <option value="A">Cámara A</option>
                              <option value="B">Cámara B</option>
                              <option value="both">A + B</option>
                            </select>
                          ) : (
                            hasA
                              ? <span className="inline-flex items-center gap-1 text-xs font-medium text-primary bg-primary/10 px-2 py-0.5 rounded"><CameraIcon className="w-2.5 h-2.5" />Sí</span>
                              : <span className="text-xs text-muted-foreground">—</span>
                          )}
                        </td>
                        <td className="px-3 py-3">
                          {!isEditing && (
                            hasB
                              ? <span className="inline-flex items-center gap-1 text-xs font-medium text-primary bg-primary/10 px-2 py-0.5 rounded"><CameraIcon className="w-2.5 h-2.5" />Sí</span>
                              : <span className="text-xs text-muted-foreground">—</span>
                          )}
                        </td>
                        <td className="px-3 py-3">
                          <StatusBadge color={color} size="sm" />
                        </td>
                        <td className="px-3 py-3">
                          <div>
                            <p className="text-xs font-medium text-foreground">{formatRelativeTime(dot.lastUpdate)}</p>
                            <p className="text-[10px] text-muted-foreground font-mono">{formatTimestamp(dot.lastUpdate)}</p>
                          </div>
                        </td>
                        <td className="px-3 py-3">
                          <div className="flex items-center gap-1">
                            {isEditing ? (
                              <>
                                <button
                                  onClick={() => handleInlineSave(dot)}
                                  className="flex items-center gap-1 bg-primary text-primary-foreground rounded px-2 py-1 text-xs font-medium hover:bg-primary/90 transition-all active:scale-95"
                                >
                                  <CheckCircleIcon className="w-2.5 h-2.5" />
                                  Guardar
                                </button>
                                <button
                                  onClick={() => setEditingId(null)}
                                  className="text-muted-foreground hover:text-foreground transition-colors p-1 rounded hover:bg-muted"
                                >
                                  <XMarkIcon className="w-3 h-3" />
                                </button>
                              </>
                            ) : (
                              <>
                                <button
                                  onClick={() => handleRefresh(dot)}
                                  title="Actualizar timestamp"
                                  className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-all"
                                >
                                  <ArrowPathIcon className="w-3.5 h-3.5" />
                                </button>
                                <button
                                  onClick={() => handleInlineEdit(dot)}
                                  title="Editar cámara"
                                  className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-all"
                                >
                                  <PencilSquareIcon className="w-3.5 h-3.5" />
                                </button>
                                <button
                                  onClick={() => { setDeletingDot(dot); setShowDeleteConfirm(true); }}
                                  title="Eliminar punto"
                                  className="p-1.5 rounded-md text-muted-foreground hover:text-red-400 hover:bg-red-500/10 transition-all"
                                >
                                  <TrashIcon className="w-3.5 h-3.5" />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {processed.length > 0 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-border bg-muted/10">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <span>Mostrar</span>
                <select
                  value={perPage}
                  onChange={e => { setPerPage(Number(e.target.value)); setPage(1); }}
                  className="bg-input border border-border rounded px-2 py-1 text-xs text-foreground outline-none"
                >
                  {[5, 10, 20, 50].map(n => (
                    <option key={`pp-${n}`} value={n}>{n}</option>
                  ))}
                </select>
                <span>por página · {processed.length} total</span>
              </div>
              <div className="flex items-center gap-1">
                <button
                  disabled={page === 1}
                  onClick={() => setPage(p => p - 1)}
                  className="px-2 py-1 text-xs rounded border border-border text-muted-foreground hover:text-foreground hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                >
                  ‹
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(n => (
                  <button
                    key={`page-${n}`}
                    onClick={() => setPage(n)}
                    className={`w-7 h-7 text-xs rounded border transition-all ${
                      page === n
                        ? 'bg-primary border-primary text-primary-foreground font-semibold'
                        : 'border-border text-muted-foreground hover:text-foreground hover:bg-muted'
                    }`}
                  >
                    {n}
                  </button>
                ))}
                <button
                  disabled={page === totalPages}
                  onClick={() => setPage(p => p + 1)}
                  className="px-2 py-1 text-xs rounded border border-border text-muted-foreground hover:text-foreground hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                >
                  ›
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Delete confirm modal */}
      {showDeleteConfirm && deletingDot && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          style={{ background: 'rgba(0,0,0,0.6)' }}
          onClick={() => { setShowDeleteConfirm(false); setDeletingDot(null); }}
        >
          <div
            className="bg-card border border-border rounded-xl shadow-2xl w-full max-w-sm mx-4"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-border">
              <h3 className="text-sm font-semibold text-foreground">Eliminar punto</h3>
              <button
                onClick={() => { setShowDeleteConfirm(false); setDeletingDot(null); }}
                className="text-muted-foreground hover:text-foreground p-1 rounded-md hover:bg-muted"
              >
                <XMarkIcon className="w-3.5 h-3.5" />
              </button>
            </div>
            <div className="px-5 py-4 flex flex-col gap-4">
              <div className="flex items-start gap-3 bg-red-500/10 border border-red-500/20 rounded-lg p-3">
                <ExclamationTriangleIcon className="w-4.5 h-4.5 text-red-400 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-foreground">
                    ¿Eliminar #{deletingDot.dotId} — {deletingDot.name}?
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Esta acción no se puede deshacer.
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => { setShowDeleteConfirm(false); setDeletingDot(null); }}
                  className="flex-1 border border-border rounded-lg py-2 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-all"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleDeleteConfirm}
                  className="flex-1 bg-red-500 text-white rounded-lg py-2 text-sm font-medium hover:opacity-90 transition-all active:scale-95"
                >
                  Sí, eliminar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className={`fixed bottom-4 right-4 z-50 flex items-center gap-2 rounded-lg px-4 py-3 shadow-2xl text-sm font-medium border ${
          toast.type === 'success' ? 'bg-card border-primary/30 text-foreground' : 'bg-red-500/10 border-red-500/30 text-red-400'
        }`}>
          {toast.type === 'success'
            ? <CheckCircleIcon className="w-4 h-4 text-primary shrink-0" />
            : <ExclamationTriangleIcon className="w-4 h-4 shrink-0" />
          }
          {toast.msg}
        </div>
      )}
    </div>
  );
}

// ─── Table header cell ───────────────────────────────────────────────────────
function ThCell({
  label, field, sortField, sortDir, onSort,
}: {
  label: string;
  field: SortField;
  sortField: SortField;
  sortDir: SortDir;
  onSort: (f: SortField) => void;
}) {
  const active = sortField === field;
  return (
    <th
      className="px-3 py-3 text-left cursor-pointer select-none group"
      onClick={() => onSort(field)}
    >
      <div className="flex items-center gap-1">
        <span className={`text-[11px] font-semibold uppercase tracking-wider transition-colors ${active ? 'text-primary' : 'text-muted-foreground group-hover:text-foreground'}`}>
          {label}
        </span>
        {active
          ? (sortDir === 'asc'
              ? <ChevronUpIcon className="w-2.5 h-2.5 text-primary" />
              : <ChevronDownIcon className="w-2.5 h-2.5 text-primary" />)
          : <span className="w-2.5 h-2.5 text-muted-foreground/40 text-[10px]">↕</span>
        }
      </div>
    </th>
  );
}

// ─── KPI Card ────────────────────────────────────────────────────────────────
function KpiCard({
  label, value, color,
}: {
  label: string;
  value: number;
  color: 'blue' | 'yellow' | 'red' | 'neutral';
}) {
  const colorMap = {
    blue:    { bg: 'bg-blue-500/10 border-blue-500/20', text: 'text-blue-400', label: 'text-blue-400/70' },
    yellow:  { bg: 'bg-yellow-500/10 border-yellow-500/20', text: 'text-yellow-400', label: 'text-yellow-400/70' },
    red:     { bg: 'bg-red-500/10 border-red-500/20', text: 'text-red-400', label: 'text-red-400/70' },
    neutral: { bg: 'bg-card border-border', text: 'text-foreground', label: 'text-muted-foreground' },
  }[color];

  return (
    <div className={`rounded-xl border px-3 py-3 flex flex-col gap-1 ${colorMap.bg}`}>
      <p className={`text-[11px] font-medium uppercase tracking-wider truncate ${colorMap.label}`}>{label}</p>
      <p className={`text-2xl font-bold leading-none ${colorMap.text}`}>{value}</p>
    </div>
  );
}